import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Check, X, Info } from 'lucide-react';

const ALLERGENS = [
  { name: 'Eggs', present: true, icon: '🥚', note: 'Boiled egg served daily with all meals' },
  { name: 'Gluten (Wheat)', present: true, icon: '🌾', note: 'Chapathis are made from wheat flour' },
  { name: 'Dairy / Milk', present: true, icon: '🥛', note: 'Used in paneer dishes and some curries (cream, butter)' },
  { name: 'Nuts', present: true, icon: '🥜', note: 'May be used in biryanis and certain curries' },
  { name: 'Mustard', present: true, icon: '🟡', note: 'Used in some cooking preparations' },
  { name: 'Celery', present: false },
  { name: 'Fish', present: false },
  { name: 'Crustaceans', present: false },
  { name: 'Molluscs', present: false },
  { name: 'Soya', present: false },
  { name: 'Sesame', present: false },
  { name: 'Lupin', present: false },
  { name: 'Sulphur Dioxide', present: false },
  { name: 'Peanuts', present: true, icon: '🥜', note: 'May be used in some dishes and curries' },
];

export default function Allergens() {
  const presentAllergens = ALLERGENS.filter(a => a.present);
  const absentAllergens = ALLERGENS.filter(a => !a.present);

  return (
    <div className="min-h-screen bg-[#FAF6F0]" data-testid="allergens-page">
      {/* Header */}
      <section className="bg-[#2C402E] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4">Food Safety</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-4" data-testid="allergens-title">
            Allergen Information
          </h1>
          <p className="text-[#FAF6F0]/80 text-lg max-w-2xl mx-auto">
            Your safety matters to us. Below is a full list of the 14 major allergens and their presence in our meals.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Notice */}
        <Card className="border-2 border-[#E05D3A]/30 bg-[#E05D3A]/5 mb-8" data-testid="allergen-notice">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-[#E05D3A] flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-[#2C402E] text-lg mb-2">Important Notice</p>
                <p className="text-[#4A4A4A]">
                  Our meals are prepared in a kitchen that handles eggs, dairy, gluten, nuts, peanuts, and mustard. 
                  While we take every precaution, cross-contamination may occur. If you have a severe allergy, 
                  please contact us at <a href="mailto:ananskitchenbristol@gmail.com" className="text-[#E05D3A] font-semibold hover:underline">ananskitchenbristol@gmail.com</a> before ordering.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allergens Present */}
        <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-4">Allergens Present in Our Meals</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {presentAllergens.map((allergen) => (
            <Card key={allergen.name} className="border-l-4 border-l-[#E05D3A]" data-testid={`allergen-${allergen.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{allergen.icon}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#2C402E]">{allergen.name}</p>
                      <Badge className="bg-[#E05D3A]/10 text-[#E05D3A] text-xs">Contains</Badge>
                    </div>
                    <p className="text-sm text-[#4A4A4A] mt-1">{allergen.note}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Allergens Not Present */}
        <h2 className="font-serif text-2xl font-bold text-[#2C402E] mb-4">Allergens Not Present</h2>
        <Card className="mb-10">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {absentAllergens.map((allergen) => (
                <div key={allergen.name} className="flex items-center gap-2 text-sm" data-testid={`allergen-free-${allergen.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <div className="w-5 h-5 rounded-full bg-[#2C402E]/10 flex items-center justify-center flex-shrink-0">
                    <X className="w-3 h-3 text-[#2C402E]" />
                  </div>
                  <span className="text-[#4A4A4A]">{allergen.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* UK Regulations */}
        <Card className="bg-[#2C402E] text-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <p className="font-serif font-semibold text-lg mb-2">UK Food Allergen Regulations</p>
                <p className="text-[#FAF6F0]/80 text-sm leading-relaxed">
                  In accordance with UK Food Information Regulations 2014, we provide information on the 14 major allergens. 
                  This list covers all dishes across our weekly menu including both vegetarian and non-vegetarian options. 
                  Please speak to a member of our team if you require further information about ingredients in any specific dish.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
